import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { PartnerService } from '../../../../services/partner.service';

/**
 * For a client that already uses Schenkwerk: the agency asks its owner for access.
 * The owner answers in the settings; the shop isn't credited to the agency.
 */
@Component({
  selector: 'app-request-access-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmDialogImports,
    HlmFieldImports,
    HlmInputImports,
    HlmSpinnerImports,
  ],
  providers: [provideIcons({ lucideTriangleAlert })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './request-access-dialog.component.html',
})
export class RequestAccessDialogComponent {
  private readonly partnerService = inject(PartnerService);

  readonly open = input(false);
  readonly close = output<void>();
  /** The backend's (deliberately vague) confirmation. */
  readonly sent = output<string>();

  readonly loading = signal(false);
  readonly submitted = signal(false);
  readonly error = signal<string | null>(null);

  readonly form = inject(FormBuilder).nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
  });

  get emailInvalid(): boolean {
    return this.submitted() && this.form.controls.email.invalid;
  }

  onDialogState(state: 'open' | 'closed'): void {
    if (state === 'closed' && this.open()) this.dismiss();
  }

  dismiss(): void {
    if (this.loading()) return;
    this.form.reset();
    this.submitted.set(false);
    this.error.set(null);
    this.close.emit();
  }

  submit(): void {
    if (this.loading()) return;
    this.submitted.set(true);
    this.error.set(null);
    if (this.form.invalid) return;

    this.loading.set(true);
    this.partnerService.requestAccess(this.form.getRawValue().email.trim()).subscribe({
      next: (response) => {
        this.loading.set(false);
        this.form.reset();
        this.submitted.set(false);
        this.sent.emit(response.message);
      },
      error: (err: HttpErrorResponse) => {
        this.loading.set(false);
        this.error.set(
          err.status === 429
            ? 'Zu viele Anfragen in kurzer Zeit. Bitte versuche es später erneut.'
            : 'Die Anfrage konnte nicht gesendet werden. Bitte versuche es erneut.',
        );
      },
    });
  }
}
